/**
 * <TutorPersonalizeStep> — « Personnalise ton profil » (organism).
 *
 * L'ancien `TutorProfilePersonalizer` : l'établissement et la filière d'études, le slogan à choisir
 * parmi quatre, puis « Tu apparaîtras de cette façon : » et l'aperçu de la fiche que voient les
 * familles (`<TutorProfilePreview>`), qui suit ce que le tuteur écrit. Les trois champs sont
 * obligatoires ; leurs erreurs arrivent en props.
 */
import React from "react"

import { Stack } from "../../atoms/containers/Stack"
import { TextInput } from "../../atoms/TextInput"
import { Text } from "../../atoms/typography/Text"
import { Card } from "../../molecules/Card"
import { SelectField } from "../../molecules/SelectField"
import { TutorProfilePreview } from "../../molecules/TutorProfilePreview"
import { type UISelectOption } from "../../../ports/primary/ui/types/studentAccount"
import { type UITutorProfilePreview } from "../../../ports/primary/ui/types/tutorCompte"

export type TutorPersonalizeField = "studyInstitution" | "studyField" | "sloganId"

export type TutorPersonalizeStepProps = {
    studyInstitution: string
    studyField: string
    sloganId: string
    slogans: UISelectOption[]
    onChange: (field: TutorPersonalizeField, value: string) => void
    preview: UITutorProfilePreview
    errors?: Partial<Record<TutorPersonalizeField, string>>
    disabled?: boolean
    testID?: string
}

export function TutorPersonalizeStep(props: TutorPersonalizeStepProps) {
    const { studyInstitution, studyField, sloganId, slogans, onChange, preview, errors = {}, disabled, testID } = props

    return (
        <Stack gap="4" testID={testID}>
            <Card p="4">
                <Stack gap="3">
                    <TextInput
                        label="Établissement d'études"
                        placeholder="Université Paris-Saclay"
                        value={studyInstitution}
                        onChangeText={(text) => onChange("studyInstitution", text)}
                        error={errors.studyInstitution}
                        disabled={disabled}
                    />
                    <TextInput
                        label="Filière d'étude"
                        placeholder="Licence de commerce international"
                        value={studyField}
                        onChangeText={(text) => onChange("studyField", text)}
                        error={errors.studyField}
                        disabled={disabled}
                    />
                    <SelectField
                        label="Choisis ton slogan"
                        items={slogans}
                        getItemId={(slogan) => slogan.id}
                        getItemLabel={(slogan) => slogan.label}
                        selectedIds={sloganId !== "" ? [sloganId] : []}
                        onChange={(ids) => onChange("sloganId", String(ids[0] ?? ""))}
                        error={errors.sloganId}
                        disabled={disabled}
                    />
                </Stack>
            </Card>
            <Stack gap="2">
                <Text color="text-muted">Tu apparaîtras de cette façon :</Text>
                <TutorProfilePreview preview={preview} />
            </Stack>
        </Stack>
    )
}
